import { apiClient, unwrap } from './client';

/**
 * Main-d'œuvre journalière — 29/09/2026.
 *
 * Les montants arrivent en chaînes : ce sont des centimes en `BigInt` côté
 * serveur, et un arrondi de virgule flottante sur une somme qui sort de la
 * caisse n'a aucune excuse.
 */

export type ModePaiement = 'especes' | 'mobile_money' | 'virement';

export const LIBELLE_MODE: Record<ModePaiement, string> = {
  especes:      'Espèces',
  mobile_money: 'Mobile Money',
  virement:     'Virement',
};

export interface OuvrierRegistre {
  id:               string;
  nom:              string;
  telephone:        string;
  metier:           string | null;
  photoUrl:         string | null;
  aConsentiPhoto:   boolean;
  joursCumules:     number;
  montantDuCents:   string;
  montantPayeCents: string;
  nbPrestations:    number;
}

export interface JourneeAPayer {
  id:                  string;
  ouvrier:             { id: string; nom: string; telephone: string; metier: string | null; photoUrl: string | null };
  dateActivite:        string;
  valideeLe:           string | null;
  nbJours:             string;
  tauxJournalierCents: string;
  montantCents:        string;
  tache:               string | null;
}

export interface PaiementPasse {
  id:             string;
  ouvrier:        { nom: string; telephone: string; metier: string | null };
  dateActivite:   string;
  nbJours:        string;
  montantCents:   string;
  mode:           ModePaiement;
  reference:      string | null;
  preuveUrl:      string | null;
  payeLe:         string;
  journalEntryId: string | null;
}

// ── Feuilles de semaine — 30/09/2026 ─────────────────────────────────────
//
// Le chef ouvre sa journée le matin, la clôture le soir ; la feuille de la
// semaine est ce que le DAF valide et ce que la caisse paie en une fois.

export interface JourneeFeuille {
  id:           string;
  dateActivite: string;
  statut:       'ouverte' | 'cloturee';
  equipe:       { id: string; code: string; nom: string; categorie: string | null } | null;
  montantCents: string;
  presences:    Array<{ id: string; ouvrier: { id: string; nom: string }; mode: 'tache' | 'journee'; nbJours: string | null; montantCents: string | null }>;
  productions:  Array<{ produitId: string; code: string; libelle: string; quantite: string; montantCents: string }>;
}

export interface FeuilleSemaine {
  feuilleId:          string;
  lundi:              string;
  statut:             'en_cours' | 'validee';
  valideeLe:          string | null;
  chef:               { id: string; nom: string };
  /** Ceux qui ont saisi les journées de la feuille — le chef de site aujourd'hui. */
  chefs:              Array<{ id: string; nom: string }>;
  equipes:            Array<{ id: string; code: string; nom: string; categorie: string | null }>;
  journees:           JourneeFeuille[];
  nbJourneesOuvertes: number;
  produits:           Array<{ produitId: string; code: string; libelle: string; quantite: number; montantCents: string }>;
  ouvriers:           Array<{
    ouvrierId: string;
    ouvrier:   { id: string; nom: string; telephone: string; metier: string | null; photoUrl: string | null };
    jours: number; joursEquipe: number; joursJournalier: number;
    montantCents: string; regleCents: string | null;
  }>;
  totalCents: string;
  regleCents: string;
  resteCents: string;
}

export interface SemaineBureau {
  lundi:    string;
  dimanche: string;
  feuilles: FeuilleSemaine[];
  resume:   { nbFeuilles: number; aValider: number; totalCents: string; regleCents: string; resteCents: string };
}

export interface ReglementSemaine {
  id:           string;
  ouvrier:      { nom: string; telephone: string; metier: string | null };
  semaine:      string;
  chef:         string;
  montantCents: string;
  mode:         ModePaiement;
  reference:    string | null;
  preuveUrl:    string | null;
  payeLe:       string;
}

export const mainOeuvreApi = {
  semaine: (date?: string) =>
    apiClient.get('/main-oeuvre/semaines', { params: date ? { date } : undefined }).then(unwrap<SemaineBureau>),

  validerSemaine: (id: string) =>
    apiClient.post(`/main-oeuvre/semaines/${id}/valider`).then(unwrap<FeuilleSemaine>),

  rouvrirSemaine: (id: string) =>
    apiClient.post(`/main-oeuvre/semaines/${id}/rouvrir`).then(unwrap<FeuilleSemaine>),

  payerSemaine: (id: string, body: { mode: ModePaiement; reference?: string; preuveUrl?: string; ouvrierIds?: string[] }) =>
    apiClient
      .post(`/main-oeuvre/semaines/${id}/payer`, body)
      .then(unwrap<{ ecriture: string; nbOuvriers: number; montantCents: string }>),

  reglementsSemaine: () =>
    apiClient.get('/main-oeuvre/reglements-semaine').then(unwrap<ReglementSemaine[]>),

  ouvriers: () =>
    apiClient.get('/main-oeuvre/ouvriers').then(unwrap<OuvrierRegistre[]>),

  aPayer: () =>
    apiClient
      .get('/main-oeuvre/a-payer')
      .then(unwrap<{ data: JourneeAPayer[]; resume: { nombre: number; totalCents: string } }>),

  paiements: () =>
    apiClient.get('/main-oeuvre/paiements').then(unwrap<PaiementPasse[]>),

  payer: (id: string, body: { mode: ModePaiement; reference?: string; preuveUrl?: string }) =>
    apiClient
      .post(`/main-oeuvre/prestations/${id}/payer`, body)
      .then(unwrap<{ id: string; montantCents: string; ecriture: string }>),

  /** Dépose la photo de la liste émargée et renvoie son URL signée. */
  deposerPreuve: (body: { dataBase64: string; contentType: string; nomOrigine?: string }) =>
    apiClient
      .post('/main-oeuvre/pieces', body)
      .then(unwrap<{ url: string; path: string; tailleOctets: number }>),
};

/**
 * Réduit une photo avant l'envoi. Une photo de téléphone moderne pèse 4 Mo,
 * la limite du serveur est à 3 Mo, et une liste émargée reste lisible en
 * 1600 px de large. On redimensionne donc plutôt que de laisser l'envoi
 * échouer sur la taille au moment où le caissier a l'argent en main.
 */
export async function reduirePhoto(fichier: File, maxDim = 1600, qualite = 0.75): Promise<{
  dataBase64: string; contentType: string;
}> {
  const bitmap = await createImageBitmap(fichier);
  const ratio  = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width  = Math.round(bitmap.width * ratio);
  canvas.height = Math.round(bitmap.height * ratio);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Impossible de préparer la photo sur cet appareil.');
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close?.();
  return { dataBase64: canvas.toDataURL('image/jpeg', qualite), contentType: 'image/jpeg' };
}
